import React, {ReactElement, useMemo, useContext} from 'react';
import {Route, Switch, Redirect, useHistory} from 'react-router-dom';
import {
  HomeIcon,
  Settings1Icon,
  ColorIcon,
  ShirtIcon
} from '@customink/pigment-react';
import {useAppSelector, useShouldForwardToPDP} from '../../hooks';
import {
  useGetStoreProductsQuery,
  useGetStoreDetailsQuery
} from '../../api/storesApi';
import {selectStoreType} from '../../slices/storesSlice';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import EditStore from '../../components/EditStore';
import Dashboard from '../../components/Dashboard';
import StoreProducts from '../../components/StoreProducts';
import StoreSettings from '../../components/StoreSettings';
import NotFound from '../../components/NotFound';
import './index.scss';
import StoreStatusBar from '../../components/StoreStatusBar';
import GroupStatusBar from '../../components/GroupStatusBar';
import OrganizerLayoutWrapper from '../../containers/OrganizerLayoutWrapper';
import StoreAnalytics from '../../components/AnalyticsDashboard';
import {MobileContext} from '../../containers/MobileProvider';

export interface Props {
  storeSlug: string;
}

const renderPageContent = (slug: string) => {
  return (
    <Switch>
      <Route exact path={`/${slug}/manage`}>
        <EditStore storeSlug={slug} />
      </Route>
      <Route path={`/${slug}/manage/dashboard`}>
        <OrganizerLayoutWrapper>
          <Dashboard storeSlug={slug} />
        </OrganizerLayoutWrapper>
      </Route>
      <Route path={`/${slug}/manage/products`}>
        <OrganizerLayoutWrapper>
          <StoreProducts storeSlug={slug} />
        </OrganizerLayoutWrapper>
      </Route>
      <Route path={`/${slug}/manage/settings`}>
        <OrganizerLayoutWrapper>
          <StoreSettings storeSlug={slug} />
        </OrganizerLayoutWrapper>
      </Route>
      <Route path={`/${slug}/manage/analytics`}>
        <OrganizerLayoutWrapper>
          <StoreAnalytics storeSlug={slug} />
        </OrganizerLayoutWrapper>
      </Route>
      <Route path={`/${slug}/manage/*`}>
        <Redirect to={`/${slug}/manage/dashboard`} />
      </Route>
      <Route>
        <NotFound />
      </Route>
    </Switch>
  );
};

const OrganizerLayout: React.FC<Props> = ({storeSlug}: Props): ReactElement => {
  useGetStoreProductsQuery(storeSlug);
  useGetStoreDetailsQuery(storeSlug);

  const history = useHistory();
  const currentPath = history.location.pathname;
  const storeProductsEmpty = useAppSelector(
    state => state.stores.storeProducts?.length === 0
  );
  const storeType = useAppSelector(selectStoreType);
  const shouldForwardToPDP = useShouldForwardToPDP();
  const mobile = useContext(MobileContext);
  const storeDetails = useAppSelector(state => state.stores.storeDetails);

  const statusBar =
    storeType === 'selfServe' ? (
      <StoreStatusBar storeSlug={storeSlug} />
    ) : (
      <GroupStatusBar storeSlug={storeSlug} />
    );

  const sidebarLinks = useMemo(() => {
    const isClosedGOF =
      storeDetails && storeDetails.status === 'closed' && storeType === 'event';

    const links = [
      {
        target: `/${storeSlug}/manage/dashboard`,
        iconAlt: 'Dashboard',
        className: 'organizer-sidebar-dashboard-button',
        icon: <HomeIcon />,
        isSelected: currentPath === `/${storeSlug}/manage/dashboard`,
        isBadgeShown: false,
        isShown: true
      },
      {
        target: `/${storeSlug}/manage/settings`,
        iconAlt: 'Settings',
        className: 'organizer-sidebar-settings-button',
        icon: <Settings1Icon />,
        isSelected: currentPath === `/${storeSlug}/manage/settings`,
        isBadgeShown: false,
        isDisabled: false,
        isShown: true
      },
      {
        target: `/${storeSlug}/manage`,
        iconAlt: 'Customization',
        className: 'organizer-sidebar-edit-button',
        icon: <ColorIcon />,
        isSelected: currentPath === `/${storeSlug}/manage`,
        isBadgeShown: false,
        isDisabled: false,
        isShown:
          !mobile && shouldForwardToPDP === false && !storeDetails?.eventStore
      },
      {
        target: `/${storeSlug}/manage/products`,
        iconAlt: 'Products',
        className: 'organizer-sidebar-products-button',
        icon: <ShirtIcon />,
        isSelected: currentPath === `/${storeSlug}/manage/products`,
        isBadgeShown: storeProductsEmpty,
        isDisabled: false,
        isShown:
          storeType === 'selfServe' ||
          (shouldForwardToPDP === false && !isClosedGOF) ||
          (storeDetails?.eventStore &&
            storeDetails?.addFromCatalog &&
            storeDetails.status !== 'closed')
      }
    ];
    return (
      <Sidebar.Container>
        {links.map(sidebarLink => {
          const {
            isDisabled,
            isSelected,
            isShown,
            className,
            target,
            icon,
            iconAlt
          } = sidebarLink;

          return (
            isShown && (
              <Sidebar.Link
                key={`${iconAlt}_key`}
                className={className}
                targetLink={target}
                isDisabled={isDisabled}
                label={iconAlt}
                icon={icon}
                isSelected={isSelected}
              />
            )
          );
        })}
      </Sidebar.Container>
    );
  }, [
    storeSlug,
    storeProductsEmpty,
    currentPath,
    storeType,
    mobile,
    shouldForwardToPDP,
    storeDetails
  ]);

  const layoutClasses = `organizer-layout${mobile ? ' mobile' : ''}`;

  return (
    <div className={layoutClasses}>
      {sidebarLinks}
      <div className="organizer-main-panel">
        <Header storeSlug={storeSlug} />
        <div className="container">
          {statusBar}
          {renderPageContent(storeSlug)}
        </div>
      </div>
    </div>
  );
};

export default OrganizerLayout;
